import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

export interface ConvertOptions {
  includeHours?: boolean;
}

export function convertMillisecondsToReadableDuration(
  milliseconds: number,
  options?: ConvertOptions,
): string {
  const hours = Math.floor(milliseconds / (1000 * 60 * 60));
  const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

  const paddedHours = hours.toString().padStart(2, "0");
  const paddedMinutes = minutes.toString().padStart(2, "0");
  const paddedSeconds = seconds.toString().padStart(2, "0");

  const parts: string[] = [];
  if (options?.includeHours || hours > 0) parts.push(paddedHours);
  parts.push(paddedMinutes);
  parts.push(paddedSeconds);
  return parts.join(":");
}

export function timesToDuration(startedAt: Date, stoppedAt?: Date): number {
  if (startedAt) {
    if (stoppedAt) {
      return Math.abs(dayjs(stoppedAt).diff(dayjs(startedAt)));
    }

    return Math.abs(dayjs().diff(dayjs(startedAt)));
  }

  return 0;
}

function createTimeStyleFormatter(locale: string | string[] | undefined) {
  return new Intl.DateTimeFormat(locale, { timeStyle: "short" });
}

const defaultTimeStyleFormatter = createTimeStyleFormatter(undefined);

/**
 * Formats a time for display. Pass `locale` (e.g. `navigator.language`) so wall-clock
 * session times use the user's 12/24h and digit rules. For `HH:mm` strings, a fixed UTC
 * calendar date is used so the value is a wall clock, not the host’s time zone.
 */
export function formatTime(time: string | Date, locale?: string | string[]) {
  if (time instanceof Date) {
    return (locale === undefined ? defaultTimeStyleFormatter : createTimeStyleFormatter(locale)).format(time);
  }
  const match = time.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (match) {
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours <= 23 && minutes <= 59) {
      const date = new Date(Date.UTC(2000, 0, 1, hours, minutes, 0));
      return new Intl.DateTimeFormat(locale, {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "UTC",
      }).format(date);
    }
  }
  const date = new Date(`1/1/1 ${time}`);
  return (locale === undefined ? defaultTimeStyleFormatter : createTimeStyleFormatter(locale)).format(date);
}

export function formatDate(date: Date | string | null | undefined) {
  return date ? dayjs(date).format("ll") : "never";
}

/** Month-year range for membership period history (e.g. "Jan 2024 - Present"). */
export function formatMembershipPeriodLabel(
  startedAt: Date | string | null | undefined,
  endedAt?: Date | string | null,
): string {
  const start = startedAt ? dayjs(startedAt).format("MMM YYYY") : "—";
  const end = endedAt ? dayjs(endedAt).format("MMM YYYY") : "Present";
  return `${start} - ${end}`;
}

export function formatDateAndTime(date: Date | string | null | undefined) {
  return date ? dayjs(date).format("lll") : "never";
}

/** Short date+time for occurred-at timestamps; empty string when missing. */
export function formatOccurredAt(date: Date | string | null | undefined): string {
  return date ? dayjs(date).format("lll") : "";
}

export function compareDateStringOrNull(a: string | null | undefined, b: string | null | undefined): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
}

export function formatRelativeTime(pastDate: dayjs.ConfigType) {
  const now = dayjs();
  const past = dayjs(pastDate);
  const diffDays = now.diff(past, "day");

  if (now.isSame(past, "day")) return "today";
  // Returns the full name of the day of the week
  if (diffDays <= 7) return `last ${past.format("dddd")}`;
  // This will return something like "6 months ago"
  if (diffDays <= 365) return past.fromNow();
  return "more than a year ago";
}

/**
 * Format a date range nicely
 * If start and end dates are on the same day, show a single date with start and end times
 * Otherwise, show start date and time and end date and time
 */
export function dateAndTime(date: string | Date): [string, string] {
  const parsedDate = dayjs(date);
  return [parsedDate.format("ddd, MMM D, YYYY"), parsedDate.format("h:mm A")];
}
